import React, { useEffect, useRef, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "./ui/sheet";
import { Input } from "./ui/input";
import { Send, MessageCircle, Image as ImageIcon, X, Sparkles } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Order Chat Sheet (Phase 5 + Section 3/4 enhancements).
 *
 * Reads from /orders/{id}/thread. Sends via /thread/send (text-only) or
 * /thread/send-rich when a photo is attached. Vendors see smart-reply
 * chips loaded from /orders/{id}/quick-replies.
 */
export default function OrderChatSheet({ order, currentUserId, open, onOpenChange }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [quickReplies, setQuickReplies] = useState([]);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const isVendorViewer = order && currentUserId && currentUserId !== order.buyer_id;

  const load = async () => {
    if (!order) return;
    try {
      const { data } = await api.get(`/orders/${order.order_id}/thread`);
      setMessages(data.messages || []);
    } catch { /* ignore */ }
  };

  const loadQuickReplies = async () => {
    if (!order || !isVendorViewer) return;
    try {
      const { data } = await api.get(`/orders/${order.order_id}/quick-replies`);
      setQuickReplies(data.replies || []);
    } catch { /* buyer gets 403 — handled */ }
  };

  useEffect(() => {
    if (!open) return;
    load();
    loadQuickReplies();
    const interval = setInterval(load, 4000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [open, order?.order_id]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  const pickFile = () => fileInputRef.current?.click();

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/orders/${order.order_id}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachment(data);
      toast.success("Photo attached — add a note and send");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't attach photo");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const sendCore = async (bodyText) => {
    const t = (bodyText ?? text).trim();
    if (!t && !attachment) return;
    setSending(true);
    try {
      const path = attachment ? "/thread/send-rich" : "/thread/send";
      const payload = attachment
        ? { text: t || null, image_file_id: attachment.id }
        : { text: t };
      const { data } = await api.post(`/orders/${order.order_id}${path}`, payload);
      setMessages((m) => [...m, data]);
      setText("");
      setAttachment(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send");
    } finally {
      setSending(false);
    }
  };

  const send = async (e) => {
    e?.preventDefault();
    await sendCore();
  };

  const otherParty = currentUserId === order?.buyer_id ? `@${order?.vendor_name}` : order?.buyer_name;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-zinc-950 border-l border-white/10 text-white w-full sm:max-w-md flex flex-col p-0"
        data-testid="chat-sheet"
      >
        <SheetHeader className="px-5 py-4 border-b border-white/10">
          <SheetTitle className="flex items-center gap-2 text-lg">
            <MessageCircle className="w-5 h-5 text-pink-400" /> {otherParty}
          </SheetTitle>
          <p className="text-xs text-zinc-500">Order #{order?.order_id?.slice(-6).toUpperCase()}</p>
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {messages.length === 0 && (
            <div className="text-center py-12 text-zinc-500 text-sm" data-testid="empty-chat">
              No messages yet. Say hi 👋
            </div>
          )}
          {messages.map((m) => {
            const mine = m.sender_id === currentUserId;
            const isIssue = m.kind === "issue_report";
            const isResolve = m.kind === "issue_resolve";
            if (isIssue || isResolve) {
              return (
                <div key={m.message_id} className="flex justify-center" data-testid={`chat-msg-${m.message_id}`}>
                  <div className={`max-w-[90%] px-3 py-2 rounded-xl text-xs ${isResolve ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-100" : "bg-red-500/10 border border-red-500/30 text-red-100"}`}>
                    <p className="font-bold uppercase tracking-wider text-[10px] mb-0.5 opacity-80">{isResolve ? "Issue resolved" : "Issue reported"}</p>
                    <p>{m.text}</p>
                  </div>
                </div>
              );
            }
            return (
              <div
                key={m.message_id}
                className={`flex ${mine ? "justify-end" : "justify-start"}`}
                data-testid={`chat-msg-${m.message_id}`}
              >
                <div
                  className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${
                    mine ? "bg-pink-500 text-white rounded-br-md" : "bg-white/[0.06] text-white rounded-bl-md"
                  }`}
                >
                  <p className="text-[10px] uppercase tracking-widest font-bold opacity-70 mb-0.5">
                    {mine ? "You" : m.sender_name}
                    {m.sender_role === "owner" ? " · owner" : m.sender_role === "manager" ? " · manager" : m.sender_role === "employee" ? " · staff" : m.sender_role === "driver" ? " · driver" : ""}
                  </p>
                  {m.attachment?.url && (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${m.attachment.url}?auth=${encodeURIComponent(localStorage.getItem("hg_token") || "")}`}
                      alt="attachment"
                      className="rounded-lg max-h-60 mb-1"
                      data-testid={`chat-img-${m.message_id}`}
                    />
                  )}
                  {m.text && <p className="whitespace-pre-wrap">{m.text}</p>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Smart reply chips — vendor only */}
        {isVendorViewer && quickReplies.length > 0 && (
          <div className="px-3 py-2 border-t border-white/10 flex gap-1.5 overflow-x-auto" data-testid="chat-quick-replies">
            {quickReplies.map((qr) => (
              <button
                key={qr.id}
                onClick={() => sendCore(qr.text)}
                disabled={sending}
                className="shrink-0 px-3 py-1.5 rounded-full text-xs bg-white/5 hover:bg-pink-500/20 border border-white/10 text-zinc-200 hover:border-pink-500/40 flex items-center gap-1"
                data-testid={`quick-reply-${qr.id}`}
              >
                <Sparkles className="w-3 h-3 opacity-60" />
                {qr.label}
              </button>
            ))}
          </div>
        )}

        {attachment && (
          <div className="px-4 pt-2 flex items-center gap-2 text-xs text-zinc-400">
            <ImageIcon className="w-3 h-3" />
            <span className="flex-1 truncate">Photo ready to send</span>
            <button onClick={() => setAttachment(null)} className="text-zinc-400 hover:text-white" data-testid="chat-attachment-remove">
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        <form onSubmit={send} className="px-4 py-3 border-t border-white/10 flex items-center gap-2">
          <input ref={fileInputRef} type="file" accept="image/*" onChange={onFileChosen} className="hidden" data-testid="chat-file-input" />
          <button
            type="button"
            onClick={pickFile}
            className="h-11 w-11 rounded-full bg-white/5 hover:bg-white/10 grid place-items-center"
            data-testid="chat-attach-btn"
            aria-label="Attach image"
          >
            <ImageIcon className="w-4 h-4 text-zinc-300" />
          </button>
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={attachment ? "Add a note (optional)…" : "Type a message…"}
            className="h-11 bg-white/5 border-white/10 rounded-full text-white placeholder:text-zinc-500"
            data-testid="chat-input"
          />
          <button
            type="submit"
            disabled={sending || (!text.trim() && !attachment)}
            className="h-11 w-11 rounded-full bg-pink-500 hover:bg-pink-400 grid place-items-center disabled:opacity-40"
            aria-label="Send message"
            data-testid="chat-send-btn"
          >
            <Send className="w-4 h-4 text-white" />
          </button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
